import dayjs from 'dayjs';
import React from 'react';

import IsBlocked from '@/core/components/IsBlocked';
import { UserAboutSection } from '@/core/components/ProfileDetails/UserAboutSection';
import { UserCounterSection } from '@/core/components/ProfileDetails/UserCounterSection';
import { UserInterestsSection } from '@/core/components/ProfileDetails/UserInterestsSection';
import { UserIntroductionSection } from '@/core/components/ProfileDetails/UserIntroductionSection';
import { UserPrivateProfileActionsSection, UserPublicProfileActionsSection } from '@/core/components/ProfileDetails/UserProfileActionsSection';
import { UserStatusSection } from '@/core/components/ProfileDetails/UserStatusSection';
import { useAuthContext } from '@/core/lib/auth/auth.context';
import { useFF } from '@/core/lib/ff/ff.context';
import type UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import Query from '@/core/lib/new-architecture/query';
import { FeatureFlag } from '@/core/types/featureFlag.type';

dayjs.locale('fr');

interface ProfileDetailsProps {
  user: UserDomain;
  variant?: 'small';
  canShare?: boolean;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({ user, variant = 'default', canShare }) => {
  const { accessToken } = useAuthContext();
  const { data: interestsResult } = Query.interests.useInterests({ token: accessToken });

  const { isFFactivate: hasFFSuperUser } = useFF(FeatureFlag.SUPER_USER);

  const { donationDonated, donationReceived, username, avatarUrl, city, createdAt, followersCount } = user.getPublicInfo();
  const isPrivate = user.isPrivate();

  const { isSuperUser, isNice, isPunctual } = user.status;

  const isSmall = variant === 'small';

  return (
    <article className="space-y-6 text-content-primary">
      <div className="space-y-4 max-lg:space-y-6">
        <UserIntroductionSection username={username} avatarUrl={avatarUrl} uuid={user.uuid} isSmall={isSmall} isSuperUser={isSuperUser} />
        <UserCounterSection sends={donationDonated} takes={donationReceived} totalCo2={user.totalCo2Formatted} hasCounter={isSmall} />
      </div>
      {user.isBlocked() && <IsBlocked blockedReason={user.blockedReason} />}
      {!hasFFSuperUser && <UserStatusSection isNice={isNice} isPunctual={isPunctual} isSmall={isSmall} isSuperUser={false} />}
      <UserAboutSection city={city} createdAt={createdAt} followersCount={followersCount} isSmall={isSmall} isPrivate={isPrivate} />
      {isPrivate && !isSmall && <UserInterestsSection interests={interestsResult?.interests} isSmall={isSmall} />}
      {hasFFSuperUser && (!isPrivate || isSmall) && <UserStatusSection isNice={isNice} isPunctual={isPunctual} isSmall={isSmall} isSuperUser={isSuperUser} />}
      {isPrivate ? <UserPrivateProfileActionsSection user={user} canShare={canShare} /> : !user.isBlocked() && <UserPublicProfileActionsSection user={user} canShare={canShare} />}
    </article>
  );
};
export default ProfileDetails;
